import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";
import type { FunnelTrendsMetrics, FunnelTrendsTopContent } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LabelWithTip, InfoTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import { formatPLN, cn } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

type Grain = "week" | "month";

const WEEK_PERIODS = [8, 12] as const;
const MONTH_PERIODS = [3, 6, 12] as const;

const STEP_META: {
  key: keyof Pick<FunnelTrendsMetrics, "views" | "entries" | "carts" | "purchases">;
  label: string;
  tipId: MetricTipId;
  cvrKey?: keyof FunnelTrendsMetrics["cvr"];
  deltaKey: keyof NonNullable<FunnelTrendsMetrics["delta_pct"]>;
}[] = [
  { key: "views", label: "Wyświetlenia", tipId: "funnel_views", cvrKey: "views_to_entries", deltaKey: "views" },
  { key: "entries", label: "Wejścia", tipId: "funnel_entries", cvrKey: "entries_to_carts", deltaKey: "entries" },
  { key: "carts", label: "Koszyki", tipId: "funnel_carts", cvrKey: "carts_to_purchases", deltaKey: "carts" },
  { key: "purchases", label: "Zakupy", tipId: "funnel_purchases", deltaKey: "purchases" },
];

const CONF_LABEL: Record<string, { label: string; tipId: MetricTipId; className: string }> = {
  hard: {
    label: "Przypisane via",
    tipId: "content_top_hard",
    className: "bg-emerald-100 text-emerald-900 border-emerald-200",
  },
  proximate: {
    label: "Blisko w czasie",
    tipId: "content_top_proximate",
    className: "bg-amber-100 text-amber-900 border-amber-200",
  },
  corr: {
    label: "Korelacja ruchu",
    tipId: "content_top_corr",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  },
};

const FORMAT_LABEL: Record<string, string> = {
  carousel: "Karuzela",
  reel: "Rolka",
  yt: "YouTube",
  video: "Wideo",
  image: "Obraz",
  channel: "Kanał",
};

function fmtNum(n: number | null | undefined): string {
  return Number(n || 0).toLocaleString("pl-PL");
}

function fmtPct(ratio: number | null | undefined): string {
  if (ratio == null || Number.isNaN(ratio)) return "n/d";
  return `${(ratio * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

function fmtDelta(d: number | null | undefined): { text: string; className: string } {
  if (d == null || Number.isNaN(d)) return { text: "n/d", className: "text-muted-foreground" };
  const sign = d > 0 ? "+" : "";
  const className =
    d > 0 ? "text-emerald-700" : d < 0 ? "text-rose-700" : "text-muted-foreground";
  return { text: `${sign}${d.toFixed(1)}%`, className };
}

function maxWidthPct(value: number, max: number): string {
  if (max <= 0) return "12%";
  const pct = Math.max(12, Math.min(100, (value / max) * 100));
  return `${pct}%`;
}

export default function FunnelTrendsPanel() {
  const [grain, setGrain] = useState<Grain>("week");
  const [periods, setPeriods] = useState<number>(8);
  const [selected, setSelected] = useState<string>("");

  useEffect(() => {
    setSelected("");
    setPeriods(grain === "week" ? 8 : 6);
  }, [grain]);

  const periodOptions = grain === "week" ? WEEK_PERIODS : MONTH_PERIODS;

  const query = useQuery({
    queryKey: ["funnel-trends", grain, periods, selected],
    queryFn: () =>
      api.siteAnalyticsFunnelTrends({
        grain,
        periods,
        selected: selected || undefined,
      }),
    retry: false,
  });

  const data = query.data;
  const funnel = data?.funnel;
  const previous = data?.previous;
  const series = data?.series ?? [];
  const top = data?.top_content ?? [];
  const coverage = data?.coverage;

  useEffect(() => {
    if (data?.selected && !selected) {
      setSelected(data.selected);
    }
  }, [data?.selected, selected]);

  const funnelMax = useMemo(() => {
    if (!funnel) return 1;
    return Math.max(funnel.views, funnel.entries, funnel.carts, funnel.purchases, 1);
  }, [funnel]);

  const prevMax = useMemo(() => {
    if (!previous) return 1;
    return Math.max(previous.views, previous.entries, previous.carts, previous.purchases, 1);
  }, [previous]);

  const chartData = useMemo(
    () =>
      series.map((s) => ({
        period: s.period,
        views_to_entries: s.cvr?.views_to_entries != null ? +(s.cvr.views_to_entries * 100).toFixed(3) : null,
        entries_to_carts: s.cvr?.entries_to_carts != null ? +(s.cvr.entries_to_carts * 100).toFixed(2) : null,
        carts_to_purchases:
          s.cvr?.carts_to_purchases != null ? +(s.cvr.carts_to_purchases * 100).toFixed(2) : null,
        purchases: s.purchases,
      })),
    [series],
  );

  const deltaTip: MetricTipId = grain === "week" ? "funnel_wow" : "funnel_mom";

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="trendy" />

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={grain === "week" ? "default" : "outline"} onClick={() => setGrain("week")}>
          Tydzień
        </Button>
        <Button size="sm" variant={grain === "month" ? "default" : "outline"} onClick={() => setGrain("month")}>
          Miesiąc
        </Button>
        <span className="mx-1 h-4 w-px bg-border" />
        {periodOptions.map((n) => (
          <Button
            key={n}
            size="sm"
            variant={periods === n ? "default" : "outline"}
            onClick={() => {
              setPeriods(n);
              setSelected("");
            }}
          >
            {n} {grain === "week" ? "tyg." : "mies."}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {data?.label_pl ?? "czas PL"}
          {data?.selected ? ` · wybrany: ${data.selected}` : ""}
        </span>
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Wczytywanie trendów lejka…</CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Nie udało się wczytać danych.</CardContent>
        </Card>
      )}

      {funnel && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <FunnelCard
              title={`Lejek · ${data?.selected ?? ""}`}
              tipId={deltaTip}
              funnel={funnel}
              max={funnelMax}
              showDelta
            />
            {previous ? (
              <FunnelCard
                title="Poprzedni okres"
                tipId={deltaTip}
                funnel={previous}
                max={Math.max(funnelMax, prevMax)}
                muted
              />
            ) : (
              <Card>
                <CardContent className="py-8 text-sm text-muted-foreground">
                  Brak poprzedniego okresu w wybranym zakresie (to pierwszy bucket).
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                <LabelWithTip tipId="funnel_wow" label={`Przychód i AOV · ${data?.selected ?? ""}`} />
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="text-muted-foreground">Przychód EC</div>
                <div className="text-lg font-semibold">{formatPLN(funnel.revenue)}</div>
                {funnel.delta_pct?.revenue != null && (
                  <div className={cn("text-xs", fmtDelta(funnel.delta_pct.revenue).className)}>
                    {fmtDelta(funnel.delta_pct.revenue).text} vs poprz.
                  </div>
                )}
              </div>
              <div>
                <div className="text-muted-foreground">AOV</div>
                <div className="text-lg font-semibold">{formatPLN(funnel.aov)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Zakupy</div>
                <div className="text-lg font-semibold">{fmtNum(funnel.purchases)}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">CVR w czasie (kliknij okres)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    onClick={(state) => {
                      const label = (state as { activeLabel?: string } | undefined)?.activeLabel;
                      if (label) setSelected(String(label));
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" width={42} />
                    <Tooltip
                      formatter={(value: number | string, name: string) => [
                        typeof value === "number" ? `${value}%` : value,
                        name,
                      ]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="views_to_entries"
                      name="Views→wejścia %"
                      stroke="#64748b"
                      dot={{ r: 3 }}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="entries_to_carts"
                      name="Wejścia→koszyk %"
                      stroke="#0ea5e9"
                      dot={{ r: 3 }}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="carts_to_purchases"
                      name="Koszyk→zakup %"
                      stroke="#059669"
                      dot={{ r: 3 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(data?.period_keys ?? []).map((key) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={key === (data?.selected || selected) ? "default" : "outline"}
                    onClick={() => setSelected(key)}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {coverage && coverage.share_hard != null && coverage.share_hard < 0.2 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="py-4 text-sm text-amber-950">
                {coverage.note_pl ||
                  "Mało zakupów z via. Topka hard jest cienka. Najpierw CTA z unikalnym via."}
                {" "}
                (udział hard: {((coverage.share_hard || 0) * 100).toFixed(0)}%)
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                <LabelWithTip tipId="content_top_hard" label="Top materiałów vs zakupy" />
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {top.length === 0 ? (
                <p className="text-sm text-muted-foreground">Brak materiałów do rankingu w tym zakresie.</p>
              ) : (
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-2 pr-2">#</th>
                      <th className="py-2 pr-2">Pewność</th>
                      <th className="py-2 pr-2">Materiał</th>
                      <th className="py-2 pr-2">Format</th>
                      <th className="py-2 pr-2 text-right">Zakupy hard</th>
                      <th className="py-2 pr-2 text-right">PLN hard</th>
                      <th className="py-2 pr-2 text-right">Prox.</th>
                      <th className="py-2 text-right">Δ views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((row) => (
                      <TopRow key={`${row.rank}-${row.id || row.title}`} row={row} />
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {!!data?.notes_pl?.length && (
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {data.notes_pl.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function FunnelCard({
  title,
  tipId,
  funnel,
  max,
  showDelta,
  muted,
}: {
  title: string;
  tipId: MetricTipId;
  funnel: FunnelTrendsMetrics;
  max: number;
  showDelta?: boolean;
  muted?: boolean;
}) {
  return (
    <Card className={cn(muted && "opacity-90")}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <LabelWithTip tipId={tipId} label={title} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {STEP_META.map((step, idx) => {
          const value = Number(funnel[step.key] || 0);
          const nextCvr =
            step.cvrKey && funnel.cvr ? funnel.cvr[step.cvrKey] : null;
          const delta = showDelta ? fmtDelta(funnel.delta_pct?.[step.deltaKey]) : null;
          return (
            <div key={step.key}>
              <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                <LabelWithTip tipId={step.tipId} label={step.label} className="font-medium" />
                <span className="tabular-nums font-semibold">{fmtNum(value)}</span>
              </div>
              <div className="h-7 w-full rounded-md bg-muted/60">
                <div
                  className={cn(
                    "flex h-full items-center rounded-md px-2 text-[11px] font-medium text-white",
                    idx === 0 && "bg-slate-500",
                    idx === 1 && "bg-sky-500",
                    idx === 2 && "bg-violet-500",
                    idx === 3 && "bg-emerald-600",
                  )}
                  style={{ width: maxWidthPct(value, max) }}
                />
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                {step.cvrKey && (
                  <span>
                    CVR dalej: <strong className="text-foreground">{fmtPct(nextCvr)}</strong>
                  </span>
                )}
                {delta && (
                  <span className={delta.className}>
                    vs poprz.: {delta.text}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function TopRow({ row }: { row: FunnelTrendsTopContent }) {
  const conf = CONF_LABEL[row.confidence] || CONF_LABEL.corr;
  const title = row.title || row.id || "(bez tytułu)";
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-2.5 pr-2 tabular-nums text-muted-foreground">{row.rank}</td>
      <td className="py-2.5 pr-2">
        <span className="inline-flex items-center gap-1">
          <Badge variant="outline" className={cn("font-normal", conf.className)}>
            {conf.label}
          </Badge>
          <InfoTip tipId={conf.tipId} />
        </span>
      </td>
      <td className="py-2.5 pr-2">
        {row.url ? (
          <a href={row.url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">
            {title}
          </a>
        ) : (
          title
        )}
        {row.via ? <div className="text-xs text-muted-foreground">via={row.via}</div> : null}
      </td>
      <td className="py-2.5 pr-2">{FORMAT_LABEL[row.format] || row.format}</td>
      <td className="py-2.5 pr-2 text-right tabular-nums">{fmtNum(row.purchases)}</td>
      <td className="py-2.5 pr-2 text-right tabular-nums">{formatPLN(row.revenue)}</td>
      <td className="py-2.5 pr-2 text-right tabular-nums text-muted-foreground">
        {fmtNum(row.proximate_purchases || 0)}
      </td>
      <td className="py-2.5 text-right tabular-nums text-muted-foreground">
        {fmtNum(row.views_gained || 0)}
      </td>
    </tr>
  );
}
